/**
 * Mirror EN EL FRONT de `server/src/domain/validators/row.validator.ts`.
 *
 * Lo usa el modal de Legajos bis (AgregarAdherenteModal) para validar de forma
 * INTENSA antes de agregar un adicional al legajo: marca y bloquea no solo los
 * campos vacíos sino también los que tienen un valor INVÁLIDO (ej. el clásico
 * `parentesco='FAM'`, que está "lleno" pero no es un código válido). Sin esto,
 * un campo lleno-pero-inválido no se marca, pasa el chequeo de "no vacío" y el
 * operador puede intentar agregar gente con datos mal cargados.
 *
 * El backend revalida igual con su propio `validateRow` (es la fuente de
 * verdad); este mirror evita el 422 sorpresa y, sobre todo, hace cumplir que
 * estas personas entren al legajo con TODOS los datos correctos.
 *
 * ⚠️ MANTENER SINCRONIZADO con `row.validator.ts` del server (mismas reglas y
 * catálogos). Los sets de enums se derivan de `@lib/catalogs` para no duplicar.
 */
import {
  ESTADO_CIVIL_OPCIONES,
  NACIONALIDAD_OPCIONES,
  PARENTESCO_OPCIONES,
  SEXO_OPCIONES,
  TIPO_DOC_OPCIONES,
} from './catalogs';

const SEXOS = SEXO_OPCIONES.map((o) => o.value); // ['M','F']
const NACIONALIDADES = NACIONALIDAD_OPCIONES.map((o) => o.value); // ['AR','EX']
const TIPOS_DOC = TIPO_DOC_OPCIONES.map((o) => o.value); // ['DNI','PA']
const PARENTESCOS = PARENTESCO_OPCIONES.map((o) => Number(o.value)); // [0,5,7,13]
const ESTADOS_CIVILES = ESTADO_CIVIL_OPCIONES.map((o) => o.value); // ['S','C','P']

const RE_NUMERICO = /^\d+$/;
const RE_ALFABETICO = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s'.-]+$/;
const RE_LEGAJO = /^[A-Za-z0-9-]+$/;
const RE_FECHA = /^\d{2}\/\d{2}\/\d{4}$/;

const PARENTESCO_TITULAR = '0';

/**
 * Mirror de `cuilContieneDocumento` del server (`alta-rules.ts`, VEN-637): el
 * cuerpo del CUIL (dígitos 3 a 10) tiene que ser el DNI completado con ceros a
 * la izquierda. Solo se afirma algo con un DNI argentino de 7 u 8 dígitos y un
 * CUIL de 11; con pasaporte o valores que ya fallan por forma devuelve `true`
 * (eso lo marca cada regla por su lado).
 */
export function cuilContieneDni(
  cuil: string | null | undefined,
  documento: string | null | undefined,
  tipoDoc: string | null | undefined,
): boolean {
  const c = (cuil ?? '').trim();
  if (!/^\d{11}$/.test(c)) return true;
  const tipo = (tipoDoc ?? '').trim().toUpperCase();
  if (tipo && tipo !== 'DNI') return true;
  const dni = (documento ?? '').trim();
  if (!/^\d{7,8}$/.test(dni)) return true;
  return c.slice(2, 10) === dni.padStart(8, '0');
}

function emailValido(s: string): boolean {
  if (!s) return false;
  if (/\s/.test(s)) return false;
  const at = s.indexOf('@');
  if (at <= 0 || at !== s.lastIndexOf('@')) return false;
  const domain = s.slice(at + 1);
  if (!domain) return false;
  const dot = domain.lastIndexOf('.');
  return dot > 0 && dot < domain.length - 1;
}

type ClaseFecha = 'ok' | 'formato' | 'anterior-1900' | 'futura';

/** Hoy en hora ARGENTINA como YYYYMMDD (-3h, sin horario de verano): no depende del huso de la PC. */
function hoyArgentinaInt(ahora: Date): number {
  const art = new Date(ahora.getTime() - 3 * 60 * 60 * 1000);
  return art.getUTCFullYear() * 10000 + (art.getUTCMonth() + 1) * 100 + art.getUTCDate();
}

/**
 * Mirror de `clasificarFecha` del server (`alta-rules.ts`): rango 1900..HOY en hora
 * argentina, con un solo parseo y una sola lectura del reloj. El tope era el año en
 * curso, así que el modal aceptaba fechas futuras del mismo año.
 */
export function clasificarFecha(s: string, ahora = new Date()): ClaseFecha {
  if (!RE_FECHA.test(s)) return 'formato';
  const [dd, mm, yyyy] = s.split('/').map(Number);
  // UTC + setUTCFullYear: sin husos y sin que Date convierta los años 0-99 en 19xx.
  const d = new Date(Date.UTC(2000, mm - 1, dd));
  d.setUTCFullYear(yyyy);
  if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) {
    return 'formato';
  }
  if (yyyy < 1900) return 'anterior-1900';
  return yyyy * 10000 + mm * 100 + dd > hoyArgentinaInt(ahora) ? 'futura' : 'ok';
}

type Raw = Record<string, string>;

interface Regla {
  field: string;
  required: boolean;
  requiredIf?: (raw: Raw) => boolean;
  maxLen?: number;
  validate?: (v: string, raw: Raw) => string | null;
}

const REGLAS: Regla[] = [
  {
    field: 'legajo',
    required: true,
    maxLen: 20,
    validate: (v) => (RE_LEGAJO.test(v) ? null : 'Solo letras, números y guiones (sin espacios)'),
  },
  {
    field: 'parentesco',
    required: true,
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n)) return 'Debe ser numérico';
      return PARENTESCOS.includes(n) ? null : `Parentesco "${v}" no es válido`;
    },
  },
  {
    field: 'apellSolt',
    required: true,
    maxLen: 30,
    validate: (v) => (RE_ALFABETICO.test(v) ? null : 'Solo letras'),
  },
  {
    field: 'nombres',
    required: true,
    maxLen: 30,
    validate: (v) => (RE_ALFABETICO.test(v) ? null : 'Solo letras'),
  },
  {
    field: 'fechaNacimiento',
    required: true,
    validate: (v) => {
      const clase = clasificarFecha(v);
      if (clase === 'ok') return null;
      return clase === 'futura' ? 'No puede ser una fecha futura' : 'Formato dd/mm/aaaa o fecha inválida';
    },
  },
  {
    field: 'nacionalidad',
    required: true,
    validate: (v) => (NACIONALIDADES.includes(v.toUpperCase()) ? null : 'Debe ser AR o EX'),
  },
  {
    field: 'sexo',
    required: true,
    validate: (v) => (SEXOS.includes(v.toUpperCase()) ? null : 'Debe ser M o F'),
  },
  {
    field: 'estadoCivil',
    required: true,
    validate: (v) =>
      ESTADOS_CIVILES.includes(v.toUpperCase()) ? null : `Estado civil "${v}" no es válido`,
  },
  {
    field: 'tipoDoc',
    required: true,
    validate: (v) => (TIPOS_DOC.includes(v.toUpperCase()) ? null : 'Debe ser DNI o PA'),
  },
  {
    field: 'documento',
    required: true,
    maxLen: 15,
    validate: (v, raw) => {
      const tipo = (raw.tipoDoc ?? '').toUpperCase();
      if (tipo === 'DNI' && !RE_NUMERICO.test(v)) return 'DNI debe ser numérico';
      if (v.includes('.')) return 'No debe contener puntos';
      return null;
    },
  },
  {
    field: 'cuil',
    required: true,
    maxLen: 11,
    validate: (v, raw) => {
      if (/[.-]/.test(v)) return 'Sin puntos ni guiones';
      if (!RE_NUMERICO.test(v)) return 'Solo dígitos';
      if (v.length !== 11) return 'Debe tener 11 dígitos';
      // Pedido de MS (VEN-637): mismo mensaje que el padrón.
      if (!cuilContieneDni(v, raw.documento, raw.tipoDoc)) return `No contiene al DNI ${raw.documento}`;
      return null;
    },
  },
  { field: 'domicilio', required: true, maxLen: 90 },
  { field: 'numero', required: false, maxLen: 7 },
  { field: 'piso', required: false, maxLen: 15 },
  { field: 'depto', required: false, maxLen: 15 },
  {
    field: 'cp',
    required: false,
    validate: (v) => {
      // El CP "0"/"0000" no se puede borrar después en Binary → se rechaza.
      if (/^0+$/.test(v)) return 'El CP no puede ser 0';
      return /^\d{1,4}$/.test(v) ? null : 'CP debe ser numérico (máx 4)';
    },
  },
  {
    field: 'areaCelular',
    required: false,
    requiredIf: (raw) => !!(raw.numeroCelular ?? '').trim(),
    maxLen: 4,
    validate: (v) => {
      if (!RE_NUMERICO.test(v)) return 'Debe ser numérico';
      if (v.startsWith('0')) return 'Sin ceros a la izquierda';
      return null;
    },
  },
  {
    field: 'numeroCelular',
    required: false,
    maxLen: 8,
    validate: (v) => {
      if (!RE_NUMERICO.test(v)) return 'Debe ser numérico';
      if (v.startsWith('15')) return 'Sin el 15 a la izquierda';
      return null;
    },
  },
  {
    field: 'email',
    required: true,
    requiredIf: (raw) => {
      const p = (raw.parentesco ?? '').trim();
      return p === '' || p === PARENTESCO_TITULAR;
    },
    maxLen: 60,
    validate: (v) => (emailValido(v) ? null : 'Email con formato inválido'),
  },
];

/**
 * Valida un único campo contra su regla. Devuelve el mensaje de error o null
 * si pasa. Extraído de `validarFilaAsociado` para mantener su complejidad
 * cognitiva bajo el umbral de Sonar (S3776) — mismo patrón que el
 * `validateField` del server.
 */
function validarCampo(regla: Regla, value: string, raw: Raw): string | null {
  if (!value) {
    const obligatorio = regla.requiredIf ? regla.requiredIf(raw) : regla.required;
    return obligatorio ? 'Campo obligatorio' : null;
  }
  if (regla.maxLen && value.length > regla.maxLen) return `Excede ${regla.maxLen} caracteres`;
  return regla.validate ? regla.validate(value, raw) : null;
}

/** Cross-field nacionalidad ↔ tipoDoc (AR→DNI, EX→PA). Null si no aplica. */
function errorNacionalidadTipoDoc(raw: Raw): string | null {
  const nac = (raw.nacionalidad ?? '').trim().toUpperCase();
  const tipo = (raw.tipoDoc ?? '').trim().toUpperCase();
  if (!nac || !tipo) return null;
  if (nac === 'AR' && tipo !== 'DNI') return 'Para nacionalidad AR el documento debe ser DNI.';
  if (nac === 'EX' && tipo !== 'PA') return 'Para nacionalidad EX el documento debe ser PA (pasaporte).';
  return null;
}

/**
 * Devuelve un mapa `campo -> mensaje` con TODOS los campos inválidos de la fila
 * (vacío-obligatorio o formato inválido). Vacío = la fila es válida.
 */
export function validarFilaAsociado(raw: Raw): Record<string, string> {
  const errores: Record<string, string> = {};
  for (const regla of REGLAS) {
    const value = (raw[regla.field] ?? '').toString().trim();
    const err = validarCampo(regla, value, raw);
    if (err) errores[regla.field] = err;
  }
  // Espejo de `parentesco.adherenteNoHabilitado` del server (02/10/2026): en
  // esta etapa no se dan de alta adherentes. El 13 sigue en el catálogo para
  // mostrar a los que ya están; lo que se frena es ingresar uno nuevo.
  if (!errores.parentesco && (raw.parentesco ?? '').trim() === '13') {
    errores.parentesco =
      'El parentesco 13 (Adherente) no está habilitado en esta etapa: ' +
      'solo se pueden dar de alta titular (0), cónyuge (5) e hijos (7).';
  }
  // Cross-field solo si ambos campos están y sin error previo.
  if (!errores.tipoDoc && !errores.nacionalidad) {
    const cruzado = errorNacionalidadTipoDoc(raw);
    if (cruzado) errores.tipoDoc = cruzado;
  }
  return errores;
}
